import React from 'react';

import clsx from 'clsx';

import {
    makeStyles,
    Drawer,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    IconButton,
} from '@material-ui/core';

import { Link, useLocation } from 'react-router-dom';

import { X } from 'lucide-react';


export interface MobileNavigationItem {
    title: string;
    to?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
}

export interface MobileNavigationProps {
    open: boolean;
    onClose: () => void;
    items: MobileNavigationItem[];
    footerItems?: React.ReactNode[];
    title?: string;
    side?: 'left' | 'right' | 'bottom';
    width?: number | string;
}


export const useStyles = makeStyles(() => ({
    paper: (props: any) => ({
        width: props.width,
        background: '#050505',
        border: 'none',
        top: "80px",
    }),
    drawer: {
        top: "80px !important"
    },
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
    },

    title: {
        color: '#fff',
        fontSize: 32,
        fontFamily: `'Cormorant Garamond', serif`,
    },

    closeButton: {
        color: '#fff',
    },

    content: {
        overflowY: 'auto',
        padding: '40px 21px',
        margin: "auto 0 0 0"
    },

    footer: {
        marginTop: 80,
        display: "flex",
        flexDirection: "column",
        gap: 12
    },

    item: {
        padding: '14px 20px',
        transition: '0.3s',
        borderBottom: "1px solid #1F1F1F",
        '&:hover': {
            background: 'rgba(255,255,255,0.05)',
        },
    },

    itemActive: {
        background: 'rgba(255,255,255,0.08)',
    },

    icon: {
        color: '#FFFFFF',
        minWidth: 42,
    },

    text: {
        color: '#FFFFFF',
        fontSize: "16px",
        fontWeight: 400,
    },
}));


export const MobileNavigation: React.FC<
    MobileNavigationProps
> = ({
    open,
    onClose,
    items,
    footerItems = [],
    side = 'left',
    width = 320,
}) => {
        const classes = useStyles({ width });

        const location = useLocation();

        const renderItem = (item: any) => {
            const isActive =
                item.to &&
                (
                    location.pathname === item.to ||
                    (
                        item.to !== '/' &&
                        location.pathname.startsWith(item.to)
                    )
                );

            return (
                <ListItem
                    button
                    key={item.title}
                    component={item.to ? Link : 'button'}
                    to={item.to}
                    onClick={() => {
                        item.onClick?.();
                        onClose();
                    }}
                    className={clsx(classes.item, {
                        [classes.itemActive]: isActive,
                    })}
                >
                    {item.icon && (
                        <ListItemIcon className={classes.icon}>
                            {item.icon}
                        </ListItemIcon>
                    )}

                    <ListItemText
                        primary={item.title}
                        classes={{
                            primary: classes.text,
                        }}
                    />
                </ListItem>
            );
        };

        return (
            <Drawer
                anchor={side}
                open={open}
                onClose={onClose}
                hideBackdrop
                classes={{
                    paper: classes.paper,
                    root: classes.drawer,
                }}
            >
                <Box className={classes.root}>
                    <Box className={classes.content}>
                        <List>
                            {items.map(renderItem)}
                        </List>
                        {!!footerItems.length && (
                            <Box className={classes.footer}>
                                {footerItems.map((item, index) => <div key={index}>{item}</div>)}
                            </Box>
                        )}
                    </Box>

                </Box>
            </Drawer>
        );
    };